import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useStore } from '@/store';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// We'll load Stripe dynamically with the publishable key from the backend
let stripePromise: Promise<any> | null = null;

const loadStripeWithKey = (publishableKey: string) => {
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

function CheckoutForm() {
  const [isLoading, setIsLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { cart } = useStore();
  const location = useLocation();
  const { amount, email, name, paymentIntentId } = location.state || {};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/cart`,
          payment_method_data: {
            billing_details: {
              email: email,
              name: name
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment failed');
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        // Store cart data in localStorage before redirect
        localStorage.setItem('pendingStripeCart', JSON.stringify(cart));
        
        navigate('/cart', {
          state: {
            orderId: paymentIntentId,
            paymentStatus: 'success',
            paymentMethod: 'stripe'
          },
        });
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement options={{
        defaultValues: {
          billingDetails: {
            name: name,
            email: email
          }
        }
      }} />
      <Button
        type="submit"
        disabled={!stripe || isLoading}
        className="w-full"
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Processing...
          </div>
        ) : (
          `Pay ${formatPrice(amount)}`
        )}
      </Button>
    </form>
  );
}

export function StripePaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  
  const { clientSecret, amount, email, name, publishableKey } = location.state || {};

  useEffect(() => {
    if (!clientSecret || !amount || !email) {
      navigate('/cart');
      return;
    }
  }, [clientSecret, amount, email, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Payment Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/cart')}
            className="w-full"
          >
            Return to Cart
          </Button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <Loader2 className="h-12 w-12 animate-spin text-brand-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Initializing Payment
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we set up your payment...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Complete Payment - Store</title>
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Complete Your Payment
          </h1>
          
          <Elements 
            stripe={loadStripeWithKey(publishableKey)} 
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
              },
            }}
          >
            <CheckoutForm />
          </Elements>
        </div>
      </div>
    </>
  );
}